import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Hammer, Lock, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import ProductSearchBox from "./ProductSearchBox";

export default function Header() {
    const [open, setMobileOpen] = useState(false);
    const [cats, setCats] = useState([]);
    const [settings, setSettings] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    const onHeaderQueryChange = (v) => {
        if (location.pathname === "/productos") {
            const params = new URLSearchParams(location.search);
            if (v.trim()) params.set("q", v.trim());
            else params.delete("q");
            const qs = params.toString();
            navigate({ pathname: "/productos", search: qs ? `?${qs}` : "" }, { replace: true });
        }
    };

    return (
        <header className="sticky top-0 z-40 w-full min-w-0 bg-[var(--lz-navy)] text-white border-b border-white/10" data-testid="site-header">
            <div className="bg-[var(--lz-orange)] text-[var(--lz-navy)]">
                <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-widest">
                    <span className="hidden sm:inline">{settings?.tagline || "Catálogo profesional para construcción"}</span>
                    <span className="sm:hidden">Ferretería Lozmar</span>
                    {settings?.phone && (
                        <span className="hidden md:inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {settings.phone}</span>
                    )}
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-2 sm:gap-4 min-w-0">
                <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                    <span className="inline-flex items-center justify-center w-9 h-9 bg-[var(--lz-orange)] text-[var(--lz-navy)]">
                        <Hammer className="w-5 h-5" strokeWidth={2.5} />
                    </span>
                    <div className="leading-none">
                        <div className="font-display text-xl font-bold uppercase tracking-tight">Ferretería</div>
                        <div className="font-display text-[11px] font-bold uppercase tracking-[0.4em] text-[var(--lz-orange)]">Lozmar</div>
                    </div>
                </Link>

                <div className="hidden md:flex flex-1 min-w-0 max-w-xl ml-4 lg:ml-6" data-testid="header-search-form">
                    <ProductSearchBox
                        variant="header"
                        showStock={false}
                        limit={8}
                        placeholder="Buscar martillo, taladro, cemento…"
                        onQueryChange={onHeaderQueryChange}
                        inputTestId="header-search-input"
                    />
                </div>

                <nav className="hidden lg:flex items-center gap-6 ml-auto text-sm font-bold uppercase tracking-wider">
                    <NavLink to="/" end className={({ isActive }) => `hover:text-[var(--lz-orange)] ${isActive ? "text-[var(--lz-orange)]" : ""}`} data-testid="nav-home">Inicio</NavLink>
                    <NavLink to="/productos" className={({ isActive }) => `hover:text-[var(--lz-orange)] ${isActive ? "text-[var(--lz-orange)]" : ""}`} data-testid="nav-products">Productos</NavLink>
                    <div className="relative group" data-testid="nav-cats">
                        <button type="button" className="hover:text-[var(--lz-orange)]">Categorías</button>
                        <div className="absolute top-full right-0 mt-2 w-72 bg-[var(--lz-navy)] border border-white/15 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition">
                            <div className="grid grid-cols-1 divide-y divide-white/10">
                                {cats.map((c) => (
                                    <Link
                                        key={c.id}
                                        to={`/categoria/${c.slug}`}
                                        className="px-4 py-2.5 text-xs hover:bg-[var(--lz-orange)] hover:text-[var(--lz-navy)]"
                                        data-testid={`nav-cat-${c.slug}`}
                                    >
                                        {c.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                    <NavLink to="/contacto" className={({ isActive }) => `hover:text-[var(--lz-orange)] ${isActive ? "text-[var(--lz-orange)]" : ""}`} data-testid="nav-contact">Contacto</NavLink>
                </nav>

                <Link
                    to="/admin/login"
                    className="ml-auto lg:ml-4 inline-flex items-center gap-2 px-3 py-2 border-2 border-[var(--lz-orange)] text-[var(--lz-orange)] hover:bg-[var(--lz-orange)] hover:text-[var(--lz-navy)]"
                    data-testid="header-admin-link"
                    title="Acceso administrador"
                >
                    <Lock className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider hidden sm:inline">Admin</span>
                </Link>

                <button type="button" onClick={() => setMobileOpen((o) => !o)} className="lg:hidden p-2 border-2 border-white" aria-label="Menu" data-testid="mobile-menu-btn">
                    {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10 bg-[var(--lz-navy)]">
                    <div className="p-4">
                        <ProductSearchBox
                            variant="header"
                            showStock={false}
                            limit={8}
                            placeholder="Buscar..."
                            onQueryChange={onHeaderQueryChange}
                            inputTestId="mobile-search-input"
                        />
                    </div>
                    <div className="grid">
                        <NavLink onClick={() => setMobileOpen(false)} to="/" end className="px-4 py-3 border-t border-white/10 text-sm font-bold uppercase">Inicio</NavLink>
                        <NavLink onClick={() => setMobileOpen(false)} to="/productos" className="px-4 py-3 border-t border-white/10 text-sm font-bold uppercase">Productos</NavLink>
                        <NavLink onClick={() => setMobileOpen(false)} to="/contacto" className="px-4 py-3 border-t border-white/10 text-sm font-bold uppercase">Contacto</NavLink>
                        {cats.map((c) => (
                            <Link
                                key={c.id}
                                onClick={() => setMobileOpen(false)}
                                to={`/categoria/${c.slug}`}
                                className="px-4 py-3 border-t border-white/10 text-xs uppercase"
                            >
                                {c.name}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
